/**
 * My Schedule: the teacher's own week, built from the classes they teach.
 *
 * This is deliberately not the class-level "Schedule" section, which is school
 * setup and belongs to admins. This one answers "where am I supposed to be?" —
 * every block of theirs laid out by weekday, in time order, for the term they
 * are teaching.
 */
import { useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import TermFilter from './TermFilter';
import type { TeachingClass, TeachingContext } from './teachingContext';
import { ALL_TERMS, classesInTerm, currentTermId, defaultTermId, termsFromClasses } from './teachingTerms';
import { WEEKDAYS, WEEKDAY_LABELS, formatClockTime, type Weekday } from '../Schedules/scheduleUtils';

/** Monday first: nobody plans their teaching week starting on Sunday. */
const WEEK_ORDER: Weekday[] = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY',
];

interface Meeting {
  key: string;
  classOffering: TeachingClass;
  startsAt: string;
  endsAt: string;
  timeLabel: string;
  classroom?: string | null;
  oneTimeDate?: string | null;
}

/** One weekday of the teacher's week. A day is small enough to just read. */
function DayCard({
  label, meetings, schoolSlug, yearSlug,
}: {
  label: string;
  meetings: Meeting[];
  schoolSlug?: string;
  yearSlug?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{label}</CardTitle>
        <CardDescription>
          {meetings.length === 1 ? '1 class' : `${meetings.length} classes`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {meetings.map((meeting) => (
          <Link
            key={meeting.key}
            to={`/app/${schoolSlug}/years/${yearSlug}/courses/${meeting.classOffering.course?.slug}/classes/${meeting.classOffering.id}`}
            className="block rounded-lg border p-3 transition-colors hover:border-primary"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-medium">{meeting.classOffering.displayName}</p>
              <Badge variant="secondary" className="shrink-0">
                {formatClockTime(meeting.startsAt)}
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">{meeting.timeLabel}</p>
            <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              {meeting.classroom ?? 'Location not assigned'}
            </p>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

function TeachingSchedulePage() {
  const { schoolSlug, yearSlug } = useParams();
  const { classes, loading, schoolYearName } = useOutletContext<TeachingContext>();

  const terms = useMemo(() => termsFromClasses(classes), [classes]);
  const current = useMemo(() => currentTermId(terms), [terms]);
  const fallback = useMemo(() => defaultTermId(terms), [terms]);
  const [chosenTerm, setChosenTerm] = useState<string | null>(null);
  const selectedTerm = chosenTerm ?? fallback;

  const visible = useMemo<TeachingClass[]>(
    () => (selectedTerm ? classesInTerm(classes, selectedTerm) : [...classes]),
    [classes, selectedTerm]
  );

  // A block repeating Mon/Wed/Fri is one record but three places to be, so it
  // is expanded into one meeting per weekday before anything is sorted.
  const byWeekday = useMemo(() => {
    const week = new Map<Weekday, Meeting[]>();
    for (const offering of visible) {
      for (const block of offering.scheduleBlocks ?? []) {
        const meeting = (weekday: Weekday): Meeting => ({
          key: `${block.id}-${weekday}`,
          classOffering: offering,
          startsAt: block.startsAt,
          endsAt: block.endsAt,
          timeLabel: block.timeLabel,
          classroom: block.classroom?.name ?? null,
          oneTimeDate: block.oneTime && block.oneTimeDate ? String(block.oneTimeDate) : null,
        });
        const weekdays = block.oneTime
          ? []
          : (block.weekdays as ReadonlyArray<string>).filter((day): day is Weekday =>
            (WEEKDAYS as ReadonlyArray<string>).includes(day));
        for (const weekday of weekdays) {
          const list = week.get(weekday) ?? [];
          list.push(meeting(weekday));
          week.set(weekday, list);
        }
      }
    }
    for (const list of week.values()) {
      list.sort((first, second) => first.startsAt.localeCompare(second.startsAt));
    }
    return week;
  }, [visible]);

  const days = WEEK_ORDER.filter((weekday) => (byWeekday.get(weekday)?.length ?? 0) > 0);

  if (loading && classes.length === 0) {
    return <Card><CardHeader><CardTitle>Loading…</CardTitle></CardHeader></Card>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">My Schedule</h2>
          <p className="text-muted-foreground">Where you are expected, week by week.</p>
        </div>
        <TermFilter
          terms={terms}
          value={selectedTerm ?? ALL_TERMS}
          onChange={setChosenTerm}
          currentTermId={current}
        />
      </div>

      {days.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Nothing scheduled</CardTitle>
            <CardDescription>
              {classes.length === 0
                ? `You aren't assigned to any classes in ${schoolYearName}.`
                : 'None of your classes in this term have a schedule assigned yet.'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {days.map((weekday) => (
            <DayCard
              key={weekday}
              label={WEEKDAY_LABELS[weekday]}
              meetings={byWeekday.get(weekday)!}
              schoolSlug={schoolSlug}
              yearSlug={yearSlug}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TeachingSchedulePage;
